import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../supabase'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../i18n/LanguageContext'
import { useTheme } from '../context/ThemeContext'
import { QUOTES } from '../utils/motivationalQuotes'

const NAVY = '#0B1F3A'
const TEAL = '#2DD4BF'

function timeAgo(date, t) {
  const s = Math.floor((new Date() - date) / 1000)
  if (s < 60)    return t ? t('topbar.just_now') : 'just now'
  if (s < 3600)  return t ? t('topbar.ago_min',  { n: Math.floor(s / 60) })   : Math.floor(s / 60) + 'm ago'
  if (s < 86400) return t ? t('topbar.ago_hour', { n: Math.floor(s / 3600) }) : Math.floor(s / 3600) + 'h ago'
  return t ? t('topbar.ago_day', { n: Math.floor(s / 86400) }) : Math.floor(s / 86400) + 'd ago'
}

const STORAGE_KEY = 'pc_read_notifs'
function getReadIds() { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]') } catch { return [] } }
function markRead(ids) {
  try {
    const merged = [...new Set([...getReadIds(), ...ids])].slice(-50)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
  } catch {}
}

const NOTIF_ICONS = {
  request: 'M9 2a1 1 0 000 2h2a1 1 0 100-2H9zM4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z',
  warning: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
  error:   'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z',
  success: 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
  boost:   'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z',
}

const NOTIF_STYLES = {
  request: { bg: 'var(--primary-light)',  iconBg: 'var(--primary-border)', iconColor: 'var(--primary)' },
  warning: { bg: 'var(--warning-light)',  iconBg: 'var(--warning-border)', iconColor: 'var(--warning)' },
  error:   { bg: 'var(--error-light)',    iconBg: 'var(--error-border)',   iconColor: 'var(--error)' },
  success: { bg: 'var(--success-light)',  iconBg: 'var(--success-border)', iconColor: 'var(--success)' },
  boost:   { bg: 'var(--primary-light)',  iconBg: 'var(--primary-border)', iconColor: 'var(--primary)' },
}

// ── Tara Notification Bell ────────────────────────────────────────────────────
const TARA_TYPE_CONFIG = {
  new_tara_order:       { icon: '🤖', color: '#0D9488', bg: 'rgba(13,148,136,0.1)',  label: 'New Order' },
  escalation:           { icon: '🙋', color: '#DC2626', bg: 'rgba(220,38,38,0.1)',   label: 'Needs You' },
  tara_stuck:           { icon: '⚠️', color: '#D97706', bg: 'rgba(217,119,6,0.1)',   label: 'Tara Stuck' },
  out_of_stock_inquiry: { icon: '📦', color: '#7C3AED', bg: 'rgba(124,58,237,0.1)', label: 'Out of Stock' },
  follow_up_due:        { icon: '🔄', color: '#1D4ED8', bg: 'rgba(29,78,216,0.1)',  label: 'Follow Up' },
  order_rejected:       { icon: '❌', color: '#DC2626', bg: 'rgba(220,38,38,0.1)',   label: 'Rejected' },
}

function taraTimeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
  if (diff < 60)    return `${diff}s ago`
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

function TaraBell({ seller }) {
  const navigate = useNavigate()
  const [open, setOpen]            = useState(false)
  const [notifications, setNotifs] = useState([])
  const [unreadCount, setUnread]   = useState(0)
  const [loading, setLoading]      = useState(false)
  const panelRef = useRef()

  const fetchNotifs = useCallback(async () => {
    if (!seller?.id) return
    setLoading(true)
    const { data } = await supabase
      .from('tara_notifications')
      .select('*')
      .eq('seller_id', seller.id)
      .order('created_at', { ascending: false })
      .limit(30)
    setNotifs(data || [])
    setUnread((data || []).filter(n => !n.read).length)
    setLoading(false)
  }, [seller?.id])

  useEffect(() => {
    fetchNotifs()
    const interval = setInterval(fetchNotifs, 30000)
    return () => clearInterval(interval)
  }, [fetchNotifs])

  useEffect(() => {
    if (!seller?.id) return
    const sub = supabase
      .channel('tara_notif_bell')
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public',
        table: 'tara_notifications',
        filter: `seller_id=eq.${seller.id}`,
      }, () => fetchNotifs())
      .subscribe()
    return () => supabase.removeChannel(sub)
  }, [seller?.id, fetchNotifs])

  useEffect(() => {
    const h = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  const markOneRead = async (id) => {
    await supabase.from('tara_notifications').update({ read: true, read_at: new Date().toISOString() }).eq('id', id)
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    setUnread(prev => Math.max(0, prev - 1))
  }

  const markAllRead = async () => {
    const ids = notifications.filter(n => !n.read).map(n => n.id)
    if (!ids.length) return
    await supabase.from('tara_notifications').update({ read: true, read_at: new Date().toISOString() }).in('id', ids)
    setNotifs(prev => prev.map(n => ({ ...n, read: true })))
    setUnread(0)
  }

  const handleClick = async (notif) => {
    if (!notif.read) await markOneRead(notif.id)
    setOpen(false)
    if (notif.link_type === 'order' && notif.link_id)            navigate(`/orders/${notif.link_id}`)
    else if (notif.link_type === 'conversation' && notif.link_id) navigate(`/fb-connect?tab=inbox&conv=${notif.link_id}`)
    else if (notif.link_type === 'inventory' && notif.link_id)    navigate(`/inventory/${notif.link_id}`)
  }

  const iconBtn = { width:'34px', height:'34px', borderRadius:'var(--radius-md)', background:'var(--bg-muted)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, transition:'var(--transition-fast)', position: 'relative' }

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(o => !o); if (!open) fetchNotifs() }}
        style={{ ...iconBtn, background: open ? 'var(--primary-light)' : 'var(--bg-muted)', borderColor: open ? 'var(--primary-border)' : 'var(--border)' }}
        title="Tara AI Notifications"
      >
        {/* Tara star icon */}
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M12 2C12 2 12.8 7.5 14.5 9.5C16.2 11.5 22 12 22 12C22 12 16.2 12.5 14.5 14.5C12.8 16.5 12 22 12 22C12 22 11.2 16.5 9.5 14.5C7.8 12.5 2 12 2 12C2 12 7.8 11.5 9.5 9.5C11.2 7.5 12 2 12 2Z" fill={open ? 'var(--primary)' : 'var(--text-muted)'}/>
        </svg>
        {unreadCount > 0 && (
          <span style={{ position:'absolute', top:'-4px', right:'-4px', minWidth:'16px', height:'16px', borderRadius:'var(--radius-full)', background:'#DC2626', fontSize:'9px', fontWeight:700, color:'white', display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid var(--bg-card)', padding:'0 3px' }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position:'absolute', right:0, top:'calc(100% + 8px)',
          width:'min(360px, calc(100vw - 24px))',
          borderRadius:'var(--radius-lg)', background:'var(--bg-card)',
          border:'1px solid var(--border)', boxShadow:'var(--shadow-xl)',
          overflow:'hidden', zIndex:200,
          fontFamily:'var(--font-ui)',
        }}>
          {/* Header */}
          <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', background:'var(--bg-muted)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <span style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)' }}>Tara Notifications</span>
              {unreadCount > 0 && (
                <span style={{ fontSize:'10px', fontWeight:700, padding:'1px 7px', borderRadius:'var(--radius-full)', background:'#DC2626', color:'white' }}>
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead} style={{ fontSize:'11.5px', fontWeight:600, color:'var(--primary)', background:'none', border:'none', cursor:'pointer' }}>
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight:'360px', overflowY:'auto' }}>
            {loading && notifications.length === 0 ? (
              <div style={{ padding:'24px', textAlign:'center', fontSize:'13px', color:'var(--text-muted)' }}>Loading...</div>
            ) : notifications.length === 0 ? (
              <div style={{ padding:'32px', textAlign:'center' }}>
                <div style={{ fontSize:'28px', marginBottom:'8px' }}>🤖</div>
                <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)' }}>No Tara notifications yet</div>
                <div style={{ fontSize:'12px', color:'var(--text-muted)', marginTop:'3px' }}>Tara will notify you when customers need attention</div>
              </div>
            ) : (
              notifications.map((notif, i) => {
                const cfg = TARA_TYPE_CONFIG[notif.type] || TARA_TYPE_CONFIG.tara_stuck
                return (
                  <div key={notif.id} onClick={() => handleClick(notif)}
                    style={{
                      display:'flex', alignItems:'flex-start', gap:'10px',
                      padding:'11px 16px', borderBottom:'1px solid var(--border)',
                      cursor: notif.link_type ? 'pointer' : 'default',
                      background: notif.read ? 'transparent' : 'rgba(45,212,191,0.04)',
                    }}
                    className="pc-table-row"
                  >
                    <div style={{ width:'32px', height:'32px', borderRadius:'var(--radius-sm)', background:cfg.bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:'14px' }}>
                      {cfg.icon}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'2px' }}>
                        <span style={{ fontSize:'10px', fontWeight:700, padding:'1px 6px', borderRadius:'4px', background:cfg.bg, color:cfg.color }}>
                          {cfg.label}
                        </span>
                        {!notif.read && <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:TEAL, display:'inline-block', flexShrink:0 }}/>}
                      </div>
                      <div style={{ fontSize:'12px', fontWeight:notif.read ? 500 : 700, color:'var(--text-primary)', lineHeight:1.4, marginBottom:'2px' }}>
                        {notif.title}
                      </div>
                      {notif.body && (
                        <div style={{ fontSize:'11.5px', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {notif.body}
                        </div>
                      )}
                      <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px', opacity:0.7 }}>
                        {taraTimeAgo(notif.created_at)}{notif.customer_name && ` · ${notif.customer_name}`}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div style={{ padding:'10px 16px', borderTop:'1px solid var(--border)', background:'var(--bg-muted)', textAlign:'center' }}>
              <button
                onClick={() => { setOpen(false); navigate('/fb-connect?tab=inbox') }}
                style={{ fontSize:'12px', fontWeight:600, color:'var(--primary)', background:'none', border:'none', cursor:'pointer' }}
              >
                View all in Inbox →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Global Search ─────────────────────────────────────────────────────────────
function GlobalSearch({ seller, autoFocus = false }) {
  const navigate   = useNavigate()
  const [query, setQuery]     = useState('')
  const [results, setResults] = useState({})
  const [loading, setLoading] = useState(false)
  const [open, setOpen]       = useState(false)
  const wrapperRef = useRef()
  const inputRef   = useRef()

  const PAGES = [
    { label: 'Dashboard', path: '/dashboard' }, { label: 'Orders', path: '/orders' },
    { label: 'Add Order', path: '/orders/add' }, { label: 'Inventory', path: '/inventory' },
    { label: 'Add Product', path: '/inventory/add' }, { label: 'CRM', path: '/crm' },
    { label: 'Analytics', path: '/analytics' }, { label: 'Finance', path: '/finance' },
    { label: 'Goals', path: '/goals' }, { label: 'Events', path: '/events' },
    { label: 'POS', path: '/pos' }, { label: 'Invoices', path: '/invoices' },
    { label: 'AdBoost', path: '/grow/adboost' }, { label: 'AI Studio', path: '/grow/aimodel' },
    { label: 'Website', path: '/website' }, { label: 'Settings', path: '/settings' },
    { label: 'Plans & Pricing', path: '/pricing' }, { label: 'Learn', path: '/learn' },
    { label: 'Order Tracking', path: '/track' },
  ]

  useEffect(() => {
    if (autoFocus) setTimeout(() => inputRef.current?.focus(), 50)
  }, [autoFocus])

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); inputRef.current?.focus(); inputRef.current?.select(); setOpen(true) }
      if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur() }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const search = useCallback(async (q) => {
    if (!q || q.trim().length < 1 || !seller?.id) { setResults({}); setOpen(false); return }
    setLoading(true); setOpen(true)
    const pages = PAGES.filter(p => p.label.toLowerCase().includes(q.toLowerCase())).slice(0, 3)
    const [
      ordersByCodeRes, ordersByProductNameRes, ordByCodeRes, ordByVariantRes, ordByNotesRes, ordByBranchRes, ordBySourceRes,
      productsRes, productsByCodeRes, productsByNotesRes, productsByCategoryRes,
      variantsRes, customersRes, customersByAddressRes,
      invoicesRes, invoicesByClientRes, eventsRes, goalsRes, boostsRes,
    ] = await Promise.all([
      supabase.from('orders').select('id,order_code,total_amount,status,product_name,variant_name,customers(name,phone)').eq('seller_id',seller.id).ilike('order_code',`%${q}%`).limit(4),
      supabase.from('orders').select('id,order_code,total_amount,status,product_name,variant_name,customers(name,phone)').eq('seller_id',seller.id).ilike('product_name',`%${q}%`).limit(4),
      supabase.from('orders').select('id,order_code,total_amount,status,product_name,variant_name,customers(name,phone)').eq('seller_id',seller.id).ilike('product_code',`%${q}%`).limit(2),
      supabase.from('orders').select('id,order_code,total_amount,status,product_name,variant_name,customers(name,phone)').eq('seller_id',seller.id).ilike('variant_name',`%${q}%`).limit(2),
      supabase.from('orders').select('id,order_code,total_amount,status,product_name,variant_name,customers(name,phone)').eq('seller_id',seller.id).ilike('notes',`%${q}%`).limit(2),
      supabase.from('orders').select('id,order_code,total_amount,status,product_name,variant_name,customers(name,phone)').eq('seller_id',seller.id).ilike('branch_name',`%${q}%`).limit(2),
      supabase.from('orders').select('id,order_code,total_amount,status,product_name,variant_name,customers(name,phone)').eq('seller_id',seller.id).ilike('order_source',`%${q}%`).limit(2),
      supabase.from('products').select('id,name,product_code,stock,sell_price').eq('seller_id',seller.id).or(`name.ilike.%${q}%,color.ilike.%${q}%,season.ilike.%${q}%,product_type.ilike.%${q}%`).limit(5),
      supabase.from('products').select('id,name,product_code,stock,sell_price').eq('seller_id',seller.id).or(`product_code.ilike.%${q}%,category.ilike.%${q}%`).limit(3),
      supabase.from('products').select('id,name,product_code,stock,sell_price').eq('seller_id',seller.id).or(`notes.ilike.%${q}%,additional_details.ilike.%${q}%`).limit(2),
      supabase.from('products').select('id,name,product_code,stock,sell_price').eq('seller_id',seller.id).ilike('external_barcode',`%${q}%`).limit(2),
      supabase.from('product_variants').select('id,product_id,variant_name,variant_code,variant_1_value,variant_2_value').eq('seller_id',seller.id).or(`variant_name.ilike.%${q}%,variant_code.ilike.%${q}%,variant_1_value.ilike.%${q}%,variant_2_value.ilike.%${q}%`).limit(4),
      supabase.from('customers').select('id,name,phone,district,division,thana').eq('seller_id',seller.id).or(`name.ilike.%${q}%,phone.ilike.%${q}%,facebook_url.ilike.%${q}%`).limit(4),
      supabase.from('customers').select('id,name,phone,district,division,thana').eq('seller_id',seller.id).or(`district.ilike.%${q}%,division.ilike.%${q}%,thana.ilike.%${q}%,suburb.ilike.%${q}%,road.ilike.%${q}%,country.ilike.%${q}%`).limit(4),
      supabase.from('seller_invoices').select('id,invoice_number,total_amount,client_name,client_phone,status,created_at').eq('seller_id',seller.id).or(`invoice_number.ilike.%${q}%,notes.ilike.%${q}%`).limit(3),
      supabase.from('seller_invoices').select('id,invoice_number,total_amount,client_name,client_phone,status,created_at').eq('seller_id',seller.id).or(`client_name.ilike.%${q}%,client_email.ilike.%${q}%,client_phone.ilike.%${q}%`).limit(3),
      supabase.from('seller_events').select('id,name,start_date,status,location').eq('seller_id',seller.id).or(`name.ilike.%${q}%,location.ilike.%${q}%,event_type.ilike.%${q}%`).limit(3),
      supabase.from('seller_goals').select('id,month,year,revenue_goal,order_goal,notes').eq('seller_id',seller.id).ilike('notes',`%${q}%`).limit(3),
      supabase.from('boosts').select('id,fb_campaign_name,status,budget_bdt,goal').eq('seller_id',seller.id).or(`fb_campaign_name.ilike.%${q}%,goal.ilike.%${q}%`).limit(3),
    ])

    const dedup = (arrays, key='id') => { const seen=new Set(); const out=[]; for(const a of arrays) for(const x of (a.data||[])) { if(!seen.has(x[key])) { seen.add(x[key]); out.push(x) } } return out }
    const mergedOrders = dedup([ordersByCodeRes,ordersByProductNameRes,ordByCodeRes,ordByVariantRes,ordByNotesRes,ordByBranchRes,ordBySourceRes])
    const mergedProducts = dedup([productsRes,productsByCodeRes,productsByNotesRes,productsByCategoryRes])
    const variantProductIds = (variantsRes.data||[]).map(v=>v.product_id).filter(Boolean)
    if (variantProductIds.length) {
      const {data:vp} = await supabase.from('products').select('id,name,product_code,stock,sell_price').in('id',variantProductIds).limit(4)
      const seen = new Set(mergedProducts.map(p=>p.id))
      ;(vp||[]).forEach(p => { if(!seen.has(p.id)) mergedProducts.push(p) })
    }
    const mergedCustomers = dedup([customersRes,customersByAddressRes])
    const mergedInvoices  = dedup([invoicesRes,invoicesByClientRes])

    const nr = {}
    if (pages.length)              nr.pages     = pages
    if (mergedOrders.length)       nr.orders    = mergedOrders.slice(0,6)
    if (mergedProducts.length)     nr.products  = mergedProducts.slice(0,6)
    if (mergedCustomers.length)    nr.customers = mergedCustomers.slice(0,5)
    if (mergedInvoices.length)     nr.invoices  = mergedInvoices.slice(0,4)
    if ((eventsRes.data||[]).length)  nr.events = eventsRes.data
    if ((goalsRes.data||[]).length)   nr.goals  = goalsRes.data
    if ((boostsRes.data||[]).length)  nr.boosts = boostsRes.data
    setResults(nr); setLoading(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller?.id])

  useEffect(() => { const tmr = setTimeout(() => search(query), 180); return () => clearTimeout(tmr) }, [query, search])

  const total = Object.values(results).reduce((s,a)=>s+a.length,0)
  const showDropdown = open && query.length > 0

  const STATUS_STYLES = {
    received:{bg:'var(--primary-light)',color:'var(--primary)'},pending:{bg:'var(--warning-light)',color:'var(--warning)'},
    confirmed:{bg:'#FEF3C7',color:'#92400E'},processing:{bg:'var(--success-light)',color:'var(--success)'},
    with_courier:{bg:'#F5F3FF',color:'#6D28D9'},shipped:{bg:'var(--success-light)',color:'var(--success)'},
    closed:{bg:'var(--bg-muted)',color:'var(--text-muted)'},return:{bg:'var(--error-light)',color:'var(--error)'},
  }
  const STATUS_LABELS = {received:'Received',pending:'Pending',confirmed:'Confirmed',processing:'Processing',with_courier:'Courier',shipped:'Shipped',closed:'Closed',return:'Return'}

  const GroupHeader = ({ label }) => (
    <div style={{ padding:'5px 14px 4px', fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', background:'var(--bg-muted)', borderBottom:'1px solid var(--border)', fontFamily:'var(--font-ui)' }}>
      {label}
    </div>
  )

  const ResultRow = ({ onClick, left, right, sub }) => (
    <div onClick={onClick} className="pc-table-row"
      style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 14px', borderBottom:'1px solid var(--border)', cursor:'pointer' }}>
      <div style={{ minWidth:0, flex:1 }}>
        <div style={{ fontSize:'13px', fontWeight:500, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{left}</div>
        {sub && <div style={{ fontSize:'11.5px', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginTop:'1px', fontFamily:'var(--font-body)' }}>{sub}</div>}
      </div>
      {right && <div style={{ marginLeft:'10px', flexShrink:0 }}>{right}</div>}
    </div>
  )

  return (
    <div ref={wrapperRef} style={{ flex:3, minWidth:0, position:'relative' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 12px', borderRadius: showDropdown ? 'var(--radius-md) var(--radius-md) 0 0' : 'var(--radius-md)', background:'var(--bg-muted)', border:'1px solid '+(open?'var(--border-focus)':'var(--border)'), transition:'border-color 0.15s, box-shadow 0.15s', boxShadow: open ? '0 0 0 3px var(--primary-light)' : 'none' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={open?'var(--primary)':'var(--text-muted)'} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
        </svg>
        <input ref={inputRef} type="text" value={query}
          onChange={e=>{setQuery(e.target.value);if(e.target.value.length>0)setOpen(true)}}
          onFocus={()=>{if(query.length>0)setOpen(true)}}
          placeholder="Search orders, products, customers..."
          style={{ flex:1, border:'none', outline:'none', fontSize:'13px', color:'var(--text-primary)', background:'transparent', fontFamily:'var(--font-body)' }} />
        {loading && <div style={{ width:'14px', height:'14px', borderRadius:'50%', border:'2px solid var(--primary)', borderTopColor:'transparent', animation:'spin 0.6s linear infinite', flexShrink:0 }} />}
        {query && !loading && <button onClick={()=>{setQuery('');setOpen(false)}} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'14px', padding:'0 2px' }}>×</button>}
        {!query && !autoFocus && <kbd style={{ display:'flex', alignItems:'center', padding:'2px 6px', borderRadius:'4px', background:'var(--bg-card)', border:'1px solid var(--border)', fontSize:'10px', color:'var(--text-muted)', fontFamily:'var(--font-mono)', whiteSpace:'nowrap', flexShrink:0 }}>⌘K</kbd>}
      </div>

      {showDropdown && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'var(--bg-card)', border:'1px solid var(--border-focus)', borderTop:'none', borderRadius:'0 0 var(--radius-lg) var(--radius-lg)', boxShadow:'var(--shadow-xl)', zIndex:999, maxHeight: autoFocus ? 'calc(100vh - 140px)' : '480px', overflowY:'auto' }}>
          {!loading && total === 0 && (
            <div style={{ padding:'32px', textAlign:'center' }}>
              <div style={{ width:'40px', height:'40px', borderRadius:'var(--radius-md)', background:'var(--bg-muted)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              </div>
              <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', marginBottom:'4px', fontFamily:'var(--font-ui)' }}>No results for "{query}"</div>
              <div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>Try a different term</div>
            </div>
          )}
          {results.pages?.length>0&&<div><GroupHeader label="Pages"/>{results.pages.map(r=><ResultRow key={r.path} onClick={()=>{navigate(r.path);setOpen(false);setQuery('')}} left={r.label} right={<span style={{fontSize:'10px',color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>↵</span>}/>)}</div>}
          {results.orders?.length>0&&<div><GroupHeader label="Orders"/>{results.orders.map(r=>{const ss=STATUS_STYLES[r.status]||{};return<ResultRow key={r.id} onClick={()=>{navigate('/orders/'+r.id);setOpen(false);setQuery('')}} left={<span style={{fontFamily:'var(--font-mono)',color:'var(--primary)',fontSize:'12px'}}>{r.order_code}</span>} sub={`${r.customers?.name||'—'}${r.product_name?' · '+r.product_name:''} · ৳${Number(r.total_amount||0).toLocaleString()}`} right={<span style={{fontSize:'10px',fontWeight:600,padding:'2px 7px',borderRadius:'var(--radius-full)',background:ss.bg||'var(--bg-muted)',color:ss.color||'var(--text-muted)'}}>{STATUS_LABELS[r.status]||r.status}</span>}/>})}</div>}
          {results.products?.length>0&&<div><GroupHeader label="Products"/>{results.products.map(r=><ResultRow key={r.id} onClick={()=>{navigate('/inventory/'+r.id);setOpen(false);setQuery('')}} left={r.name} sub={`${r.product_code||''} · Stock: ${r.stock??'—'} · ৳${Number(r.sell_price||0).toLocaleString()}`} right={<span style={{fontSize:'10px',fontWeight:600,padding:'2px 7px',borderRadius:'var(--radius-full)',background:r.stock===0?'var(--error-light)':r.stock<=5?'var(--warning-light)':'var(--success-light)',color:r.stock===0?'var(--error)':r.stock<=5?'var(--warning)':'var(--success)'}}>{r.stock===0?'Out':r.stock<=5?'Low':'In stock'}</span>}/>)}</div>}
          {results.customers?.length>0&&<div><GroupHeader label="Customers"/>{results.customers.map(r=><ResultRow key={r.id} onClick={()=>{navigate('/crm');setOpen(false);setQuery('')}} left={r.name} sub={r.phone}/>)}</div>}
          {results.invoices?.length>0&&<div><GroupHeader label="Invoices"/>{results.invoices.map(r=><ResultRow key={r.id} onClick={()=>{navigate('/invoices');setOpen(false);setQuery('')}} left={r.invoice_number} sub={`৳${Number(r.total_amount||0).toLocaleString()} · ${new Date(r.created_at).toLocaleDateString('en-BD',{day:'numeric',month:'short'})}`}/>)}</div>}
          {results.events?.length>0&&<div><GroupHeader label="Events"/>{results.events.map(r=><ResultRow key={r.id} onClick={()=>{navigate('/events');setOpen(false);setQuery('')}} left={r.name} sub={r.start_date?new Date(r.start_date).toLocaleDateString('en-BD',{day:'numeric',month:'short'}):''}/>)}</div>}
          {results.boosts?.length>0&&<div><GroupHeader label="AdBoost"/>{results.boosts.map(r=><ResultRow key={r.id} onClick={()=>{navigate('/grow/adboost');setOpen(false);setQuery('')}} left={r.fb_campaign_name} sub={r.goal?.replace(/_/g,' ')} right={<span style={{fontSize:'10px',fontWeight:600,padding:'2px 7px',borderRadius:'var(--radius-full)',background:r.status==='running'?'var(--success-light)':'var(--bg-muted)',color:r.status==='running'?'var(--success)':'var(--text-muted)'}}>{r.status}</span>}/>)}</div>}
          {total>0&&<div style={{padding:'8px 14px',borderTop:'1px solid var(--border)',background:'var(--bg-muted)',fontSize:'11px',color:'var(--text-muted)',textAlign:'center',fontFamily:'var(--font-body)'}}>{total} result{total!==1?'s':''} — Esc to close</div>}
        </div>
      )}
    </div>
  )
}

// ── Topbar ────────────────────────────────────────────────────────────────────
function Topbar({ title }) {
  const { seller } = useAuth()
  const { t, lang, switchLang } = useTranslation()
  const { toggleTheme, isDark } = useTheme()
  const navigate = useNavigate()

  const [notifications, setNotifications] = useState([])
  const [readIds, setReadIds]   = useState(getReadIds())
  const [showNotifs, setShowNotifs] = useState(false)
  const [showMobileSearch, setShowMobileSearch] = useState(false)
  const notifRef = useRef()
  const [lastSync, setLastSync] = useState(new Date())
  const [syncTime, setSyncTime] = useState('just now')
  const [syncing, setSyncing]   = useState(false)

  const [quote] = useState(() => QUOTES[Math.floor(Math.random()*QUOTES.length)])

  const shopName  = seller?.shop_name || ''
  const initials  = shopName.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() || 'PC'

  useEffect(() => { setSyncTime(t('topbar.just_now')) }, [t])

  useEffect(() => {
    if (seller?.id) {
      fetchNotifications()
      const timer = setInterval(fetchNotifications, 2 * 60 * 1000)
      return () => clearInterval(timer)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller])

  useEffect(() => {
    const timer = setInterval(() => setSyncTime(timeAgo(lastSync, t)), 60000)
    return () => clearInterval(timer)
  }, [lastSync, t])

  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    if (showMobileSearch) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = prev }
    }
  }, [showMobileSearch])

  const fetchNotifications = async () => {
    if (!seller?.id) return
    setSyncing(true)
    const notifs = []
    const yesterday = new Date(); yesterday.setDate(yesterday.getDate()-1)
    const today = new Date(); today.setHours(0,0,0,0)

    const [reqs, pending, lowStock, outStock, newOrders, boostNotifs] = await Promise.all([
      supabase.from('customer_order_requests').select('id,customer_name,submitted_at,product_code').eq('seller_id',seller.id).eq('status','submitted').order('submitted_at',{ascending:false}).limit(5),
      supabase.from('orders').select('id,order_code,created_at').eq('seller_id',seller.id).in('status',['received','pending']).lt('created_at',yesterday.toISOString()).limit(5),
      supabase.from('products').select('id,name,stock').eq('seller_id',seller.id).lte('stock',5).gt('stock',0).limit(5),
      supabase.from('products').select('id,name').eq('seller_id',seller.id).eq('stock',0).limit(3),
      supabase.from('orders').select('id,order_code,created_at,customers(name)').eq('seller_id',seller.id).gte('created_at',today.toISOString()).order('created_at',{ascending:false}).limit(3),
      supabase.from('boost_notifications').select('*').eq('seller_id',seller.id).eq('is_read',false).order('created_at',{ascending:false}).limit(3),
    ])

    reqs.data?.forEach(r=>notifs.push({id:'req-'+r.id,type:'request',title:t('notif.new_request'),message:(r.customer_name||'Customer')+(r.product_code?' · '+r.product_code:''),link:'/orders',time:new Date(r.submitted_at||Date.now())}))
    pending.data?.forEach(o=>notifs.push({id:'pending-'+o.id,type:'warning',title:t('notif.needs_action'),message:t('notif.pending_24h',{code:o.order_code}),link:'/orders/'+o.id,time:new Date(o.created_at)}))
    lowStock.data?.forEach(p=>notifs.push({id:'stock-'+p.id,type:'warning',title:t('notif.low_stock'),message:p.name+' — '+p.stock+' remaining',link:'/inventory/'+p.id,time:new Date()}))
    outStock.data?.forEach(p=>notifs.push({id:'outstock-'+p.id,type:'error',title:t('notif.out_of_stock'),message:p.name,link:'/inventory/'+p.id,time:new Date()}))
    newOrders.data?.forEach(o=>notifs.push({id:'new-'+o.id,type:'success',title:t('notif.new_order_today'),message:o.order_code+' from '+(o.customers?.name||'customer'),link:'/orders/'+o.id,time:new Date(o.created_at)}))
    boostNotifs.data?.forEach(n=>notifs.push({id:'boost-'+n.id,type:'boost',title:'AdBoost update',message:n.message,link:'/grow/adboost',time:new Date(n.created_at)}))

    notifs.sort((a,b)=>b.time-a.time)
    setNotifications(notifs.slice(0,12))
    setLastSync(new Date()); setSyncTime(t('topbar.just_now')); setSyncing(false)
  }

  const handleNotifClick = async (notif) => {
    setReadIds(prev=>[...new Set([...prev,notif.id])]); markRead([notif.id]); setShowNotifs(false)
    if (notif.id.startsWith('boost-')) await supabase.from('boost_notifications').update({is_read:true}).eq('id',notif.id.replace('boost-',''))
    navigate(notif.link)
  }

  const handleMarkAllRead = async () => {
    const ids = notifications.map(n=>n.id)
    setReadIds(prev=>[...new Set([...prev,...ids])]); markRead(ids)
    const boostIds = ids.filter(id=>id.startsWith('boost-')).map(id=>id.replace('boost-',''))
    if (boostIds.length) await supabase.from('boost_notifications').update({is_read:true}).in('id',boostIds)
  }

  const unreadCount = notifications.filter(n=>!readIds.includes(n.id)).length

  const iconBtn = { width:'34px', height:'34px', borderRadius:'var(--radius-md)', background:'var(--bg-muted)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0, transition:'var(--transition-fast)' }
  const iconBtnHover = { background:'var(--primary-light)', borderColor:'var(--primary-border)' }

  return (
    <div style={{ background:'var(--bg-card)', borderBottom:'1px solid var(--border)', fontFamily:'var(--font-ui)', boxShadow:'0 1px 0 var(--border)', position:'sticky', top:0, zIndex:40, flexShrink:0 }}>

      <div style={{ display:'flex', alignItems:'center', gap:'12px', padding:'0 16px', height:'64px' }}>

        {/* Page title + quote */}
        <div className="topbar-title" style={{ minWidth:0, maxWidth:'320px', flexShrink:1 }}>
          <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', letterSpacing:'-0.2px', whiteSpace:'nowrap', lineHeight:1.2, fontFamily:'var(--font-ui)' }}>{title}</div>
          <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontFamily: lang==='bn' ? 'Hind Siliguri, var(--font-body)' : 'var(--font-body)', lineHeight:1.3 }}>
            {quote[lang] || quote.en}
          </div>
        </div>

        {/* Mobile title */}
        <div className="topbar-mobile-title" style={{ display:'none', minWidth:0, flex:1 }}>
          <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-ui)' }}>{title}</div>
        </div>

        {/* Search */}
        <div className="topbar-search" style={{ flex:3, minWidth:0 }}>
          <GlobalSearch seller={seller} />
        </div>

        <div className="topbar-spacer" style={{ flex:1 }} />

        {/* Right actions */}
        <div style={{ display:'flex', alignItems:'center', gap:'6px', flexShrink:0 }}>

          {/* Mobile search */}
          <button onClick={()=>setShowMobileSearch(true)} className="topbar-mobile-search-btn" style={iconBtn} aria-label="Search">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          </button>

          {/* Live sync dot */}
          <div className="topbar-sync"
            onClick={fetchNotifications}
            title={syncing ? 'Syncing...' : 'Last sync ' + syncTime}
            style={{ width:'32px', height:'32px', borderRadius:'var(--radius-md)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}>
            <div style={{ width:'8px', height:'8px', borderRadius:'50%', background: syncing ? 'var(--warning)' : 'var(--success)', boxShadow: syncing ? 'none' : '0 0 0 2px var(--success-light)', transition:'background 0.3s' }} />
          </div>

          {/* Language toggle */}
          <button onClick={()=>switchLang(lang==='en'?'bn':'en')}
            className="topbar-lang-btn"
            style={{ ...iconBtn, width:'auto', padding:'0 10px', gap:'5px' }}
            onMouseEnter={e=>Object.assign(e.currentTarget.style,iconBtnHover)}
            onMouseLeave={e=>{ e.currentTarget.style.background='var(--bg-muted)'; e.currentTarget.style.borderColor='var(--border)' }}>
            <span style={{ fontSize:'13px' }}>{lang==='en'?'🇬🇧':'🇧🇩'}</span>
            <span className="topbar-lang-text" style={{ fontSize:'11px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>{lang==='en'?'ENG':'বাং'}</span>
          </button>

          {/* Dark mode */}
          <button onClick={toggleTheme} style={iconBtn} title={isDark?'Light mode':'Dark mode'}
            onMouseEnter={e=>Object.assign(e.currentTarget.style,iconBtnHover)}
            onMouseLeave={e=>{ e.currentTarget.style.background='var(--bg-muted)'; e.currentTarget.style.borderColor='var(--border)' }}>
            {isDark
              ? <svg width="15" height="15" viewBox="0 0 20 20" fill="var(--text-muted)"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd"/></svg>
              : <svg width="15" height="15" viewBox="0 0 20 20" fill="var(--text-muted)"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/></svg>
            }
          </button>

          {/* ── Tara AI Bell — NEW ── */}
          <TaraBell seller={seller} />

          {/* ERP Notification bell */}
          <div style={{ position:'relative' }} ref={notifRef}>
            <button onClick={()=>{setShowNotifs(!showNotifs);if(!showNotifs)fetchNotifications()}}
              style={{ ...iconBtn, background:showNotifs?'var(--primary-light)':'var(--bg-muted)', borderColor:showNotifs?'var(--primary-border)':'var(--border)', position:'relative' }}
              onMouseEnter={e=>{ if(!showNotifs) Object.assign(e.currentTarget.style,iconBtnHover) }}
              onMouseLeave={e=>{ if(!showNotifs){e.currentTarget.style.background='var(--bg-muted)';e.currentTarget.style.borderColor='var(--border)'} }}>
              <svg width="15" height="15" viewBox="0 0 20 20" fill={unreadCount>0?'var(--text-primary)':'var(--text-muted)'}>
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/>
              </svg>
              {unreadCount>0&&(
                <span style={{ position:'absolute', top:'-4px', right:'-4px', minWidth:'16px', height:'16px', borderRadius:'var(--radius-full)', background:'var(--error)', fontSize:'9px', fontWeight:700, color:'white', display:'flex', alignItems:'center', justifyContent:'center', border:'1.5px solid var(--bg-card)', padding:'0 3px' }}>
                  {unreadCount>9?'9+':unreadCount}
                </span>
              )}
            </button>

            {showNotifs && (
              <div className="animate-scale-in"
                style={{ position:'absolute', right:0, top:'calc(100% + 8px)', width:'min(340px, calc(100vw - 24px))', borderRadius:'var(--radius-lg)', background:'var(--bg-card)', border:'1px solid var(--border)', boxShadow:'var(--shadow-xl)', overflow:'hidden', zIndex:200 }}>
                <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                    <span style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>Notifications</span>
                    {unreadCount>0&&<span style={{ fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'var(--radius-full)', background:'var(--primary-light)', color:'var(--primary)' }}>{unreadCount} new</span>}
                  </div>
                  {unreadCount>0&&<button onClick={handleMarkAllRead} style={{ fontSize:'12px', fontWeight:600, color:'var(--primary)', background:'none', border:'none', cursor:'pointer', fontFamily:'var(--font-ui)' }}>Mark all read</button>}
                </div>
                <div style={{ maxHeight:'360px', overflowY:'auto' }}>
                  {notifications.length===0 ? (
                    <div style={{ padding:'32px', textAlign:'center' }}>
                      <div style={{ width:'40px', height:'40px', borderRadius:'var(--radius-md)', background:'var(--bg-muted)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}>
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="var(--success)"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                      </div>
                      <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>All caught up</div>
                      <div style={{ fontSize:'12px', color:'var(--text-muted)', marginTop:'3px', fontFamily:'var(--font-body)' }}>No new notifications</div>
                    </div>
                  ) : notifications.map(notif=>{
                    const isRead = readIds.includes(notif.id)
                    const ns = NOTIF_STYLES[notif.type]||NOTIF_STYLES.success
                    const ni = NOTIF_ICONS[notif.type]||NOTIF_ICONS.success
                    return (
                      <div key={notif.id} onClick={()=>handleNotifClick(notif)} className="pc-table-row"
                        style={{ display:'flex', alignItems:'flex-start', gap:'10px', padding:'11px 16px', borderBottom:'1px solid var(--border)', background:isRead?'transparent':ns.bg }}>
                        <div style={{ width:'32px', height:'32px', borderRadius:'var(--radius-sm)', background:isRead?'var(--bg-muted)':ns.iconBg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          <svg width="14" height="14" viewBox="0 0 20 20" fill={isRead?'var(--text-muted)':ns.iconColor}><path fillRule="evenodd" d={ni} clipRule="evenodd"/></svg>
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:'12px', fontWeight:600, color:isRead?'var(--text-muted)':'var(--text-primary)', display:'flex', alignItems:'center', gap:'6px', fontFamily:'var(--font-ui)' }}>
                            {notif.title}
                            {!isRead&&<span style={{ width:'5px', height:'5px', borderRadius:'50%', background:'var(--primary)', flexShrink:0 }}/>}
                          </div>
                          <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{notif.message}</div>
                          <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px', opacity:0.7, fontFamily:'var(--font-body)' }}>{timeAgo(notif.time,t)}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div style={{ padding:'10px 16px', borderTop:'1px solid var(--border)', background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>Updated {syncTime}</span>
                  <button onClick={fetchNotifications}
                    style={{ fontSize:'11.5px', fontWeight:600, padding:'4px 12px', borderRadius:'var(--radius-sm)', background:'var(--primary)', color:'white', border:'none', cursor:'pointer', fontFamily:'var(--font-ui)' }}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--primary-hover)'}
                    onMouseLeave={e=>e.currentTarget.style.background='var(--primary)'}>
                    Refresh
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Avatar */}
          <button onClick={()=>navigate('/settings')}
            style={{ width:'34px', height:'34px', borderRadius:'var(--radius-md)', background:NAVY, color:'white', fontSize:'11px', fontWeight:800, border:'2px solid transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-ui)', flexShrink:0, transition:'var(--transition-fast)', letterSpacing:'0.03em' }}
            onMouseEnter={e=>{e.currentTarget.style.background='#112744';e.currentTarget.style.borderColor='rgba(45,212,176,0.4)'}}
            onMouseLeave={e=>{e.currentTarget.style.background=NAVY;e.currentTarget.style.borderColor='transparent'}}>
            {initials}
          </button>
        </div>
      </div>

      {/* Trial bar */}
      {seller?.plan === 'trial' && (
        <div style={{ padding:'6px 20px', background:'var(--primary-light)', borderTop:'1px solid var(--primary-border)', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'6px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', minWidth:0 }}>
            <svg width="13" height="13" viewBox="0 0 20 20" fill="var(--primary)" style={{flexShrink:0}}><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/></svg>
            <span style={{ fontSize:'12px', color:'var(--primary)', fontWeight:500, fontFamily:'var(--font-body)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>Free trial active — upgrade anytime to unlock all features</span>
          </div>
          <button onClick={()=>navigate('/pricing')}
            style={{ fontSize:'11.5px', fontWeight:600, padding:'4px 14px', borderRadius:'var(--radius-sm)', background:'var(--primary)', color:'white', border:'none', cursor:'pointer', fontFamily:'var(--font-ui)', whiteSpace:'nowrap', flexShrink:0 }}>
            See plans
          </button>
        </div>
      )}

      {/* Mobile search overlay */}
      {showMobileSearch && (
        <div style={{ position:'fixed', inset:0, background:'var(--bg-card)', zIndex:500, display:'flex', flexDirection:'column' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', padding:'12px 16px', borderBottom:'1px solid var(--border)' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <GlobalSearch seller={seller} autoFocus/>
            </div>
            <button onClick={()=>setShowMobileSearch(false)} style={iconBtn} aria-label="Close search">
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="var(--text-muted)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l8 8M14 6l-8 8"/></svg>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .topbar-mobile-title { display: none; }
        .topbar-mobile-search-btn { display: none; }
        @media(max-width:768px) {
          .topbar-title             { display: none !important; }
          .topbar-search            { display: none !important; }
          .topbar-sync              { display: none !important; }
          .topbar-mobile-title      { display: block !important; }
          .topbar-mobile-search-btn { display: flex !important; }
          .topbar-lang-text         { display: none !important; }
          .topbar-lang-btn          { width: 34px !important; padding: 0 !important; justify-content: center !important; }
          .topbar-spacer            { display: none !important; }
        }
      `}</style>
    </div>
  )
}

export default Topbar